import * as React from "react";
import {
  ChevronDown,
  ChevronUp,
  FileText,
  Pencil,
  Calendar,
  Users,
  Clock,
  ExternalLink,
  Trash,
} from "lucide-react";
import StudentList from "./StudentList";
import { useGetSubmissionsQuery } from "../../../../store/submissionApi";
import { ActiveState, Assignment, AssignmentListProps } from "@/types";

const AssignmentList = ({
  assignments,
  onToggleExpand,
  onEdit,
  onDelete,
}: AssignmentListProps) => {
  const [activeState, setActiveState] = React.useState<ActiveState>({
    batchCode: null,
    assignmentId: null,
    expandedId: null,
  });

  const { data: submissions, isFetching } = useGetSubmissionsQuery(
    {
      batchCode: activeState.batchCode,
      assignmentId: activeState.assignmentId,
      studentId: "",
    },
    { skip: !activeState.batchCode || !activeState.assignmentId }
  );

  const handleExpand = (item: Assignment) => {
    onToggleExpand(item._id);

    if (!item.isExpanded) {
      setActiveState({
        batchCode: item.batchCode?._id,
        assignmentId: item?._id,
        expandedId: item._id,
      });
    } else {
      setActiveState({
        batchCode: null,
        assignmentId: null,
        expandedId: null,
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-4">
      {assignments?.map((item) => {
        const isExpanded = activeState?.expandedId === item?._id;
        const isDeadlinePassed =
          item.deadline && new Date(item.deadline) < new Date();

        return (
          <div
            key={item._id}
            className="border border-gray-200 shadow-sm dark:border-gray-700 rounded-xl bg-white dark:bg-black  transition-all duration-200 overflow-hidden"
          >
            {/* Assignment Header */}
            <div
              className="p-3 cursor-pointer group"
              onClick={() => handleExpand(item)}
            >
              <div className="flex items-start justify-between gap-4">
                {/* Left Section - Main Info */}
                <div className="flex-1 min-w-0">
                  {/* Title and Type Badge */}
                  <div className="flex items-center gap-3 mb-">
                    <h3 className="font-semibold text-base text-gray-900 dark:text-gray-100  transition-colors">
                      {item.title}
                    </h3>
                    <span
                      className={
                        item?.type === "assignment"
                          ? "flex items-center gap-1.5 text-xs font-semibold text-[#49ccc1e2] dark:text-[#127971] bg-teal-50 dark:bg-[#49ccc1e2] rounded-full px-3 py-1"
                          : "flex items-center gap-1.5 text-xs font-semibold text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-full px-3 py-1"
                      }
                    >
                      <FileText size={12} />
                      {item?.type.toUpperCase()}
                    </span>
                  </div>

                  {/* Info Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-3 text-sm">
                    {/* Batch */}
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <Users
                        size={16}
                        className="text-gray-400 dark:text-gray-500 flex-shrink-0"
                      />
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {item.batchCode?.bCode || "N/A"}
                      </span>
                    </div>

                    {/* Deadline */}
                    <div className="flex items-center gap-2">
                      <Calendar
                        size={16}
                        className="text-gray-400 dark:text-gray-500 flex-shrink-0"
                      />
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Deadline
                        </span>
                        <span
                          className={`font-medium ${
                            isDeadlinePassed
                              ? "text-red-600 dark:text-red-400"
                              : "text-gray-900 dark:text-gray-100"
                          }`}
                        >
                          {item.deadline
                            ? formatDate(item.deadline)
                            : "No deadline"}
                        </span>
                      </div>
                    </div>

                    {/* Attachment (only if exists) */}
                    {item.uploadedFile && (
                      <div className="flex items-center gap-2">
                        <FileText
                          size={16}
                          className="text-gray-400 dark:text-gray-500 flex-shrink-0"
                        />
                        <div className="flex flex-col min-w-0">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Attachment
                          </span>
                          <a
                            href={item.uploadedFile}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                          >
                            View File
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Task Link (only for task & if link exists) */}
                    {item.link && (
                      <div className="flex items-center gap-2">
                        <ExternalLink
                          size={16}
                          className="text-gray-400 dark:text-gray-500 flex-shrink-0"
                        />
                        <div className="flex flex-col min-w-0">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Task Link
                          </span>
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                          >
                            Open Link
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Created Date */}
                  <div className="flex items-center gap-2 mt-3 text-xs text-gray-500 dark:text-gray-400">
                    <Clock size={14} />
                    <span>
                      Created:{" "}
                      {item.createdAt ? formatDateTime(item.createdAt) : "N/A"}
                    </span>
                  </div>
                </div>

                {/* Right Section - Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(item);
                    }}
                    className="p-2 text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 rounded-lg transition-colors"
                    title="Edit Assignment"
                  >
                    <Pencil size={18} />
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(item._id);
                    }}
                    className="p-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                    title="Delete Assignment"
                  >
                    <Trash size={18} />
                  </button>

                  <button
                    className="p-2 text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    title={isExpanded ? "Collapse" : "Expand"}
                  >
                    {isExpanded ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Expanded Content - Student Submissions */}
            {isExpanded && (
              <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                {isFetching ? (
                  <div className="p-8 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 dark:border-gray-600 border-t-indigo-600 dark:border-t-indigo-400"></div>
                    <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                      Loading submissions...
                    </p>
                  </div>
                ) : submissions?.data && submissions.data.length > 0 ? (
                  <StudentList item={submissions?.data} />
                ) : (
                  <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                    <FileText
                      size={48}
                      className="mx-auto mb-3 text-gray-300 dark:text-gray-600"
                    />
                    <p className="text-sm">No submissions yet</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AssignmentList;
